import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, Check, X, Clock, Filter, Image, Eye } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [rejectModal, setRejectModal] = useState({ open: false, depositId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [screenshotModal, setScreenshotModal] = useState({ open: false, screenshot: null, depositInfo: null });

  useEffect(() => {
    fetchDeposits();
  }, [filter]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getDeposits(filter === 'all' ? null : filter);
      setDeposits(response.data.deposits);
    } catch (error) {
      console.error('Error fetching deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (depositId) => {
    try {
      await adminApi.processDeposit(depositId, { action: 'approve' });
      toast.success('Deposit approved');
      fetchDeposits();
    } catch (error) {
      toast.error('Failed to approve deposit');
    }
  };

  const handleReject = async () => {
    try {
      await adminApi.processDeposit(rejectModal.depositId, { 
        action: 'reject', 
        reason: rejectReason 
      });
      toast.success('Deposit rejected');
      setRejectModal({ open: false, depositId: null });
      setRejectReason('');
      fetchDeposits();
    } catch (error) {
      toast.error('Failed to reject deposit');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-deposits">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-green-500/10">
            <Wallet className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Deposit Management</h1>
            <p className="text-sm text-zinc-500">Approve or reject deposit requests</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-4 h-4 text-zinc-500" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] bg-black/50 border-white/10">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deposits Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : deposits.length === 0 ? (
          <div className="text-center py-12">
            <Wallet className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No deposits found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Fee</th>
                  <th>Total Paid</th>
                  <th>Network</th>
                  <th>Transaction ID</th>
                  <th>Payment Proof</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => (
                  <tr key={deposit.id}>
                    <td>
                      <div>
                        <p className="text-white font-medium">{deposit.user?.full_name}</p>
                        <p className="text-xs text-zinc-500">{deposit.user?.email}</p>
                      </div>
                    </td>
                    <td className="text-white font-medium">${deposit.amount.toFixed(2)}</td>
                    <td className="text-amber-400">
                      {deposit.fee ? `+$${deposit.fee.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-green-400 font-medium">
                      ${(deposit.total_amount || deposit.amount).toFixed(2)}
                    </td>
                    <td className="text-zinc-400">{deposit.network}</td>
                    <td className="text-zinc-400 font-mono text-xs">
                      {deposit.transaction_id.slice(0, 20)}...
                    </td>
                    <td>
                      {deposit.screenshot ? (
                        <button
                          onClick={() => setScreenshotModal({ 
                            open: true, 
                            screenshot: deposit.screenshot,
                            depositInfo: {
                              user: deposit.user?.full_name,
                              amount: deposit.amount,
                              txId: deposit.transaction_id
                            }
                          })}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                          title="View Payment Proof"
                          data-testid={`view-screenshot-${deposit.id}`}
                        >
                          <Eye className="w-3 h-3" />
                          <span className="text-xs">View</span>
                        </button>
                      ) : (
                        <span className="text-xs text-zinc-500">No proof</span>
                      )}
                    </td>
                    <td className="text-zinc-400">
                      {new Date(deposit.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <span className={`badge ${
                        deposit.status === 'approved' ? 'badge-success' :
                        deposit.status === 'rejected' ? 'badge-danger' : 'badge-warning'
                      }`}>
                        {deposit.status}
                      </span>
                    </td>
                    <td>
                      {deposit.status === 'pending' ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleApprove(deposit.id)}
                            className="p-1.5 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            title="Approve"
                            data-testid={`approve-deposit-${deposit.id}`}
                          >
                            <Check className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => setRejectModal({ open: true, depositId: deposit.id })}
                            className="p-1.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            title="Reject"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-500">Processed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => setRejectModal({ ...rejectModal, open })}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Reject Deposit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Rejection Reason</label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleReject} className="flex-1 bg-red-500 hover:bg-red-600">
                Reject Deposit
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setRejectModal({ open: false, depositId: null })}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Screenshot Modal */}
      <Dialog open={screenshotModal.open} onOpenChange={(open) => setScreenshotModal({ ...screenshotModal, open })}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Image className="w-5 h-5 text-amber-400" />
              Payment Proof
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {screenshotModal.depositInfo && (
              <div className="grid grid-cols-3 gap-4 p-3 rounded-lg bg-black/30">
                <div>
                  <p className="text-xs text-zinc-500">User</p>
                  <p className="text-sm text-white">{screenshotModal.depositInfo.user}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Amount</p>
                  <p className="text-sm text-green-400">${screenshotModal.depositInfo.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Transaction ID</p>
                  <p className="text-sm text-zinc-400 font-mono truncate">{screenshotModal.depositInfo.txId}</p>
                </div>
              </div>
            )}
            {screenshotModal.screenshot && (
              <div className="rounded-lg overflow-hidden border border-white/10">
                <img 
                  src={screenshotModal.screenshot} 
                  alt="Payment Proof" 
                  className="w-full max-h-[500px] object-contain bg-black"
                />
              </div>
            )}
            <Button 
              variant="outline" 
              onClick={() => setScreenshotModal({ open: false, screenshot: null, depositInfo: null })}
              className="w-full border-white/10"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
